import {
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  inject,
  input,
  signal,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ActivatedRoute, RouterLink } from '@angular/router';

import { MercuryFormService } from '../../../services/mercury-form.service';
import { AuthService } from '../../../services/auth.service';
import { FulfillmentUtils } from '../../../util/fulfillment-utils';
import { BUTTON_TEXTS } from '../../../constants/buttons.constants';
import {
  FormConfigDetails,
  FormConfigResponse,
  UserDetails,
} from '../../../models/mercury-form';
import { MulesoftErrorBody } from '../../../models/mulesoft-error';
import { RdsBadgeComponent, RdsButtonModule, RdsProgressSpinnerComponent } from '@rds/angular-components';
import { FaroActionDirective } from '../../../directives/faro-action.directive';
import { finalize } from 'rxjs';
import { UiService } from '../../../services/ui.service';
import { FeatureFlagService } from '../../../services/feature-flag.service';
import { MERCURY_FORM_TEXTS } from '../../../constants/mercury-form.constants';
import { extractTicketResponseMessage } from '../../../util/ticket-error';

// AWS Lambda proxy enforces a 25s upstream timeout and returns this status
// with body `{ error: "upstream_timeout", ... }`. Match both so we still
// recognise the case if the proxy switches to another 5xx status.
const HTTP_GATEWAY_TIMEOUT = 504;
const isUpstreamTimeout = (err: unknown): boolean => {
  const status = (err as { status?: number })?.status;
  const upstream = (err as { error?: { error?: string } })?.error?.error;
  return status === HTTP_GATEWAY_TIMEOUT || upstream === 'upstream_timeout';
};

// `fetchForm` delivers the envelope as a JSON string; HttpErrorResponse wraps
// it under `.error`. Handle both.
const extractMulesoftError = (err: unknown): MulesoftErrorBody | null => {
  const raw = (err as { error?: unknown })?.error ?? err;
  let body: unknown = raw;
  if (typeof raw === 'string') {
    try { body = JSON.parse(raw); } catch { return null; }
  }
  const b = body as MulesoftErrorBody;
  if (b && (b.correlationId || b.statusMessage || b.statusDetails)) {
    return b;
  }
  return null;
};

const buildBackendMessage = (baseMessage: string, mulesoft: MulesoftErrorBody): string => {
  if (!mulesoft.correlationId) return baseMessage;
  return `${baseMessage}\n\n${MERCURY_FORM_TEXTS.BACKEND_ERROR_REFERENCE_PREFIX}${mulesoft.correlationId}`;
};

// Per-offering form availability gate (INEXP-2375). Semantics:
//   ON  → the Mercury form is enabled and renders here.
//   OFF → the form is disabled; the user is redirected to the offering's
//         `destinationValue` (origin URL) and sees an error alert.
// The flag name and Unleash strategy are unchanged; only the branch meaning
// was swapped from the earlier INEXP-2379 FILM/Mercury switch design.
export const FILM_DYNAMIC_FORM_RENDERING_FLAG = 'film_inexp_2144_dynamic_form_rendering';

const REDIRECT_URL = '/focus/my-offerings';

@Component({
  selector: 'app-mercury-form',
  standalone: true,
  imports: [
    RdsButtonModule,
    RdsBadgeComponent,
    RdsProgressSpinnerComponent,
    FaroActionDirective,
    RouterLink,
  ],
  templateUrl: './mercury-form.component.html',
  styleUrls: ['./mercury-form.component.scss'],
  host: {
    '(window:message)': 'onMessage($event)'
  }
})
export class MercuryFormComponent implements OnInit {
  @ViewChild('iframeRef') iframeRef!: ElementRef<HTMLIFrameElement>;

  private readonly _authService = inject(AuthService);
  private readonly _featureFlagService = inject(FeatureFlagService);
  private readonly _mercuryFormService = inject(MercuryFormService);
  private readonly _route = inject(ActivatedRoute);
  private readonly _sanitizer = inject(DomSanitizer);
  private readonly _uiService = inject(UiService);
  readonly isFormEnabled = signal(true);

  BUTTON = BUTTON_TEXTS;
  FORM_TEXTS = MERCURY_FORM_TEXTS;

  readonly htmlContent = signal<SafeHtml | null>(null);
  rawMulesoftString: string = '';
  formId!: string;
  // Set by the offering page via navigation state; null on a shared link.
  offeringId: string | null = null;
  formConfigDetails!: FormConfigDetails;
  // Signals rather than plain fields: these are all written from HTTP callbacks,
  // and a third-party script on this page (WalkMe, which wraps the iframe and
  // patches XMLHttpRequest) can land those callbacks outside the Angular zone.
  // A plain field assigned out-of-zone updates the instance but never the view —
  // the form data arrived, yet the spinner kept turning until an unrelated click
  // forced a tick. Writing a signal schedules change detection on its own.
  readonly isSubmitting = signal(false);
  readonly isFormLoading = signal(true);
  submitError: string | null = null;
  submitSuccess = false;
  isFetchingOptions = false;

  ngOnInit(): void {
    this.formId = this._route.snapshot.paramMap.get('id')!;
    this.offeringId = (window.history.state?.['offeringId'] as string | undefined) ?? null;
    this.fetchFormConfig();
  }

  // Load the layout, isolate scripts for later
  loadHtml(formName: string): void {
    this._mercuryFormService.fetchForm(formName).subscribe({
      next: (html: string) => {
        this.rawMulesoftString = html;

        const processedHtml = FulfillmentUtils.processHtml(html);

        // Use DOM parser to strip scripts out of the layout injection pass
        const parser = new DOMParser();
        const doc = parser.parseFromString(processedHtml, 'text/html');
        doc.querySelectorAll('script').forEach(script => script.remove());

        // NOSONAR: S6268 — trust chain reviewed.
        // Source: `_mercuryFormService.fetchForm(formName)` calls the Mulesoft-
        // fronted, Ping-authenticated Mercury layout endpoint (Roche internal).
        // Never user input, URL params, or third-party origins.
        // In-sink defense: `<script>` elements are stripped from the parsed DOM
        // before bypass, so any residual script tag from a compromised source
        // would still not execute.
        this.htmlContent.set(this._sanitizer.bypassSecurityTrustHtml(doc.documentElement.innerHTML)); // NOSONAR
        this.isFormLoading.set(false);
      },
      error: (err: unknown) => {
        this.isFormLoading.set(false);
        const mulesoft = extractMulesoftError(err);
        if (mulesoft?.statusDetails) {
          // Full Mulesoft chain (e.g. PKIX trust-store failures) — surfaced
          // to console/Faro for us; the user only sees the friendly copy.
          console.info('[mercury-form] fetchForm backend error:', mulesoft);
        }
        let message: string;
        if (isUpstreamTimeout(err)) {
          message = this.FORM_TEXTS.FORM_LOAD_TIMEOUT_MESSAGE;
        } else if (mulesoft) {
          message = buildBackendMessage(this.FORM_TEXTS.FORM_LOAD_BACKEND_ERROR, mulesoft);
        } else {
          message = this.FORM_TEXTS.FORM_LOAD_ERROR;
        }
        this._uiService.showErrorAlert(this.FORM_TEXTS.FORM_LOAD_ERROR_TITLE, message, REDIRECT_URL, BUTTON_TEXTS.BACK_TO_MY_OFFERINGS, BUTTON_TEXTS.CLOSE);
      }
    });
  }

  // Inject and execute scripts
  onIframeLoad(): void {
    if (!this.rawMulesoftString || !this.iframeRef) return;

    const iframeWindow = this.iframeRef.nativeElement.contentWindow;

    FulfillmentUtils.initializeIframeScripts(iframeWindow, this.rawMulesoftString);

    console.info("--- Iframe Lifecycle Script Initialized ---");
  }

  fetchFormConfig(): void {
    this.isFormLoading.set(true);

    // Preferred path: the service-request page already fetched the list and
    // populated the cache. Use it directly — no second /formConfigDetails
    // round-trip.
    const cached = this._mercuryFormService.getFormConfigByFormId(this.formId, this.offeringId);
    if (cached) {
      void this._applySelectedForm(cached);
      return;
    }

    // Fallback for direct URL loads / refresh / deep-link where the cache is
    // empty. Fetch without an offeringId (we only have formId at this point)
    // and pick our entry from the response.
    this._mercuryFormService.getFormConfigDetails('').subscribe({
      next: (res: FormConfigResponse) => {
        const selectedForm = FulfillmentUtils.findFormConfig(
          res?.offeringFormDetails,
          this.formId,
          this.offeringId
        );

        if (!selectedForm) {
          this.isFormLoading.set(false);
          this._uiService.showErrorAlert(this.FORM_TEXTS.FORM_NOT_FOUND_TITLE, this.FORM_TEXTS.FORM_NOT_FOUND, REDIRECT_URL, BUTTON_TEXTS.BACK_TO_MY_OFFERINGS, BUTTON_TEXTS.CLOSE);
          return;
        }

        void this._applySelectedForm(selectedForm);
      },
      error: (err: unknown) => {
        this.isFormLoading.set(false);
        const mulesoft = extractMulesoftError(err);
        if (mulesoft?.statusDetails) {
          console.info('[mercury-form] getFormConfigDetails backend error:', mulesoft);
        }
        let message: string;
        if (isUpstreamTimeout(err)) {
          message = this.FORM_TEXTS.FORM_LOAD_TIMEOUT_MESSAGE;
        } else if (mulesoft) {
          message = buildBackendMessage(this.FORM_TEXTS.FORM_LOAD_BACKEND_ERROR, mulesoft);
        } else {
          message = (err as { message?: string })?.message || this.FORM_TEXTS.FORM_CONFIG_ERROR;
        }
        this._uiService.showErrorAlert(this.FORM_TEXTS.ERROR, message, REDIRECT_URL, BUTTON_TEXTS.BACK_TO_MY_OFFERINGS, BUTTON_TEXTS.CLOSE);
      },
    });
  }

  private async _applySelectedForm(selectedForm: FormConfigDetails): Promise<void> {
    this.formConfigDetails = selectedForm;

    if (selectedForm.offeringId && selectedForm.fulfillmentType) {
      const enabled = await this._featureFlagService.isEnabledWithContext(
        FILM_DYNAMIC_FORM_RENDERING_FLAG,
        { offeringId: selectedForm.offeringId, offeringType: selectedForm.fulfillmentType }
      );

      if (!enabled) {
        this.isFormEnabled.set(false);
        this.isFormLoading.set(false);
        this._uiService.showErrorAlert(
          this.FORM_TEXTS.FORM_NOT_FOUND_TITLE,
          this.FORM_TEXTS.FORM_NOT_FOUND,
          REDIRECT_URL,
          BUTTON_TEXTS.BACK_TO_MY_OFFERINGS,
          BUTTON_TEXTS.CLOSE
        );
        // Read redirect URL from navigation state set by the offering page.
        // Checked lazily here (not at construction) so tests can prime state
        // before triggering this path via fixture.detectChanges().
        const redirectUrl = window.history.state?.['redirect'] as string | undefined;
        if (redirectUrl) {
          window.open(redirectUrl, '_blank', 'noopener,noreferrer');
        }
        return;
      }
    }

    this.loadHtml(this.formId);
  }

  private handleOptions(requestId: string, formName: string, versionNo: string, payload: any, event: MessageEvent) {
    this.isFetchingOptions = true;
    this._mercuryFormService.fetchOptions(formName, versionNo, payload).subscribe({
      next: (res) => {
        // Mercury calls `.length` on every field value; null/undefined crash it.
        const cleaned = res && typeof res === 'object'
          ? Object.fromEntries(
              Object.entries(res as Record<string, unknown>).map(
                ([k, v]) => [k, v ?? []],
              ),
            )
          : res;
        // Reply to the same origin the request came from — never '*'.
        (event.source as Window)?.postMessage(JSON.stringify({ type: 'options-response', requestId, data: cleaned }), event.origin);
      },
      error: () => {
        (event.source as Window)?.postMessage(JSON.stringify({ type: 'options-response', requestId, data: {} }), event.origin);
      }
    });
  }

  // UI-DRIVEN CONTROL FOR VALIDATION AND SUBMISSION
  triggerIframeSubmit(): void {
    this.isSubmitting.set(true);
    if (!this.iframeRef) {
      this.isSubmitting.set(false);
      return;
    }

    const iframeWindow = this.iframeRef.nativeElement.contentWindow as any;
    const iframeDoc = iframeWindow.document;

    // Force dropdown sync triggers
    iframeDoc.querySelectorAll('select').forEach((select: HTMLSelectElement) => {
      if (select.value) select.dispatchEvent(new Event('change', { bubbles: true }));
    });

    // Data collection completely handled by utility
    const preValidationData = FulfillmentUtils.extractPerfectlyTypedData(iframeWindow);
    console.info("--- UI HARVESTED PAYLOAD ---", preValidationData);

    // Client-side execution validator evaluation check
    let isValid = true;
    if (typeof iframeWindow.check_required === 'function') {
      isValid = iframeWindow.check_required(true);
    } else {
      isValid = iframeDoc.getElementById('form')?.checkValidity() ?? true;
    }

    if (!isValid) {
      const errors: string[] = [];
      iframeDoc.querySelectorAll(".required-field").forEach((field: any) => {
        const idBase = field.id?.replace("-input", "").replace("-select", "");
        const label = iframeDoc.getElementById(idBase + "-label") || iframeDoc.querySelector('label[for="' + field.id + '"]');
        let labelText = label ? label.innerText.replace('*', '').trim() : (field.name || "Unknown Field");
        if (!errors.includes(labelText)) errors.push(labelText);
      });
      const formattedErrors = errors.length > 0 ? errors.join(', ') : 'several required fields';
      this._uiService.showErrorToast(this.FORM_TEXTS.FORM_INVALID_FIELD, `${this.FORM_TEXTS.FORM_MISSING_FIELDS}\n\n ${formattedErrors}`);

      this.isSubmitting.set(false);
      return;
    }

    this.submitFormData(preValidationData);
  }

  private getCurrentUser(): UserDetails {
    return {
      userId: this._authService.cId() ?? '',
      userName: this._authService.first() ?? '',
      email: this._authService.email() ?? ''
    };
  }

  // SUBMIT
  submitFormData(formData: Record<string, unknown>): void {
    if (!formData) {
      this.isSubmitting.set(false);
      return;
    }

    const payload = FulfillmentUtils.buildSubmitPayload(
      this.getCurrentUser(),
      formData,
      this.formConfigDetails
    );

    this.isSubmitting.set(true);
    this._mercuryFormService.submitForm(payload)
      .pipe(finalize(() => this.isSubmitting.set(false)))
      .subscribe({
        next: (res: any) => {
          // Check if we have an error object (Business logic failure)
          const isError = res?.message && typeof res.message === 'object' &&
            (res.message.errorResponse?.length > 0 || res.message.TicketResponseCode === '400');

          if (isError) {
            this.handleSubmissionError({ error: res });
            return;
          }

          // Success Path: Extract the string message
          const successMessage = typeof res?.message === 'string'
            ? res.message
            : (res?.message?.TicketResponseMessage || 'Success');

          // Combined Success message as per your requirements
          const fullSuccessText = `${successMessage}`;

          this._uiService.showSuccessAlert(
            'Success',
            fullSuccessText,
            REDIRECT_URL,
            this.FORM_TEXTS.PANORAMA_BACK_BUTTON
          );
        },
        error: (err: any) => this.handleSubmissionError(err)
      });
  }

  onMessage(event: MessageEvent) {
    if (!event.data) return;
    let parsed;
    try { parsed = JSON.parse(event.data); } catch { return; }
    if (parsed.type === 'options-request') {
      this.handleOptions(
        parsed.requestId,
        parsed.formName,
        parsed.versionNo,
        parsed.payload,
        event
      );
    }
  }

  // Add this helper to handle the parsing logic cleanly
  private handleSubmissionError(err: any): void {
    this.isSubmitting.set(false);

    // AWS Lambda proxy returned HTTP 504 `{error: "upstream_timeout"}` — the
    // Mulesoft flow stalled. Surface a distinct alert (not toast) because the
    // submit may still be processing downstream; a retry could create a
    // duplicate PEGA ticket.
    if (isUpstreamTimeout(err)) {
      this.submitError = this.FORM_TEXTS.FORM_SUBMIT_TIMEOUT_MESSAGE;
      this._uiService.showErrorAlert(
        this.FORM_TEXTS.FORM_SUBMIT_TIMEOUT_TITLE,
        this.FORM_TEXTS.FORM_SUBMIT_TIMEOUT_MESSAGE,
        REDIRECT_URL,
        BUTTON_TEXTS.BACK_TO_MY_OFFERINGS,
        BUTTON_TEXTS.CLOSE,
      );
      return;
    }

    // Normalize: get the body from the error object
    const errorBody = err?.error || err;
    const errorList = errorBody?.message?.errorResponse;
    const ticketReason = extractTicketResponseMessage({ error: errorBody });

    let displayMessage = '';
    let toastTitle = this.FORM_TEXTS.FORM_INVALID_FIELD;

    // Check if we have the specific field validation errors
    if (Array.isArray(errorList) && errorList.length > 0) {
      // Map each error to the format: [Field Name]
      const formattedErrors = errorList.map((item: any) => {
        const formattedField = item.fieldName
          .replace(/_/g, ' ')
          .replace(/\b\w/g, (l: string) => l.toUpperCase());
        return `${formattedField}`;
      }).join(',');

      displayMessage = `${this.FORM_TEXTS.FORM_MISSING_FIELDS}: \n${formattedErrors}`;
    } else if (ticketReason) {
      // Show ServiceNow/PEGA's own reason (e.g. "Catalog Item: '' is
      // inactive.") — the generic copy gave users and support nothing to act on.
      console.info('[mercury-form] backend rejected submit:', errorBody.message);
      displayMessage = `${ticketReason} ${this.FORM_TEXTS.FORM_SUBMIT_REJECTED_HINT}`;
      toastTitle = this.FORM_TEXTS.FORM_SUBMIT_REJECTED_TITLE;
    } else {
      const mulesoft = extractMulesoftError(err);
      if (mulesoft) {
        if (mulesoft.statusDetails) {
          console.info('[mercury-form] submitForm backend error:', mulesoft);
        }
        displayMessage = buildBackendMessage(this.FORM_TEXTS.FORM_SUBMIT_BACKEND_ERROR, mulesoft);
      } else {
        // Generic failure (network drop, unknown response shape)
        displayMessage = this.FORM_TEXTS.FORM_SUBMIT_FAILED_LATER;
      }
      toastTitle = this.FORM_TEXTS.SUBMISSION_ERROR;
    }

    this.submitError = displayMessage;
    this._uiService.showErrorToast(toastTitle, displayMessage);
  }
}
